import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import {
  markAppReady,
  markAppNotReady,
  shouldDeferPrerender,
} from "../utils/clientRenderMode";
import { isReactSnapCrawler } from "../utils/prerenderData";
import {
  prefetchRoute,
  routeNeedsPrefetch,
} from "../routes/routePrefetchRegistry";

const DeferredNavigationContext = createContext(null);

export function DeferredNavigationProvider({ children }) {
  const location = useLocation();
  const auth = useContext(AuthContext);
  const deferEnabled = shouldDeferPrerender() && !isReactSnapCrawler();

  const authStateRef = useRef({
    loading: auth.loading,
    isAuthenticated: auth.isAuthenticated,
    player: auth.player,
  });
  const authReadyResolversRef = useRef(new Set());

  useEffect(() => {
    authStateRef.current = {
      loading: auth.loading,
      isAuthenticated: auth.isAuthenticated,
      player: auth.player,
    };

    if (!auth.loading) {
      for (const resolve of authReadyResolversRef.current) {
        resolve();
      }
      authReadyResolversRef.current.clear();
    }
  }, [auth]);

  const waitForAuth = useCallback(async () => {
    if (!authStateRef.current.loading) return;
    await new Promise((resolve) => {
      authReadyResolversRef.current.add(resolve);
      if (!authStateRef.current.loading) {
        authReadyResolversRef.current.delete(resolve);
        resolve();
      }
    });
  }, []);

  const authRef = useRef(null);
  if (!authRef.current) {
    authRef.current = {
      get authLoading() {
        return authStateRef.current.loading;
      },
      get isAuthenticated() {
        return authStateRef.current.isAuthenticated;
      },
      get player() {
        return authStateRef.current.player;
      },
      waitForAuth,
    };
  }

  const [displayLocation, setDisplayLocation] = useState(() =>
    deferEnabled ? null : location
  );
  const pendingRef = useRef(null);
  const isInitialRef = useRef(true);

  const syncDisplayLocation = useCallback(
    async (targetLocation) => {
      if (!deferEnabled) {
        setDisplayLocation(targetLocation);
        markAppReady();
        return;
      }

      const needsPrefetch = routeNeedsPrefetch(targetLocation.pathname);

      if (needsPrefetch) {
        if (isInitialRef.current) {
          markAppNotReady();
        }
        await prefetchRoute(
          targetLocation.pathname,
          targetLocation.search,
          authRef.current
        );
      }

      setDisplayLocation(targetLocation);
      markAppReady();
    },
    [deferEnabled]
  );

  useEffect(() => {
    if (!deferEnabled) {
      setDisplayLocation(location);
      return undefined;
    }

    const targetKey = `${location.pathname}${location.search}${location.key}`;
    if (
      displayLocation &&
      `${displayLocation.pathname}${displayLocation.search}${displayLocation.key}` ===
        targetKey
    ) {
      return undefined;
    }

    if (pendingRef.current === targetKey) {
      return undefined;
    }

    pendingRef.current = targetKey;

    let cancelled = false;
    (async () => {
      await syncDisplayLocation(location);
      if (!cancelled) {
        pendingRef.current = null;
        isInitialRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location drives sync
  }, [location, deferEnabled, syncDisplayLocation]);

  const value = useMemo(
    () => ({
      displayLocation: displayLocation || location,
      isDeferred: deferEnabled && displayLocation === null,
      isInitialLoad: isInitialRef.current,
    }),
    [displayLocation, location, deferEnabled]
  );

  return (
    <DeferredNavigationContext.Provider value={value}>
      {children}
    </DeferredNavigationContext.Provider>
  );
}

export function useDeferredNavigation() {
  return useContext(DeferredNavigationContext);
}

export default DeferredNavigationProvider;
